import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { HelpCircle, Loader2 } from 'lucide-react';
import { AuthLayout, BackToSignInLink } from '@/components/layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { accountApi } from '@/resources/account';
import { forgotPasswordSchema } from '@/schemas';
import { ForgotPasswordParams } from '@/types';
import { cn } from '@/lib/utils';

// AUT-39: business rule 34 -- the token lives 30 minutes, and
// `VerifyPasswordResetToken::TTL_MINUTES` (backend) is the one home for that
// number. This string is the only place in the product that copies the
// numeral instead of reading the constant (components/account.md § The SPA);
// a task that changes the TTL changes both.
const TOKEN_TTL_MINUTES = 30;

// FR-14: a UI-only affordance, not a server-enforced cooldown -- business
// rule 35 is unconditional rotation, and `config/auth.php`'s throttle is
// inert (constraints.md § C-26).
const RESEND_COOLDOWN_SECONDS = 60;

type View = 'form' | 'sent';

const ForgotPassword = () => {
  const [view, setView] = useState<View>('form');
  const [sentEmail, setSentEmail] = useState('');
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ForgotPasswordParams>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  // Fires on both a genuine 200 and a masked 429 (account.api.ts) -- the
  // screen must show the same confirmation either way, so it adds no
  // onError of its own here.
  const handleSent = () => {
    setView('sent');
    setSecondsRemaining(RESEND_COOLDOWN_SECONDS);
  };

  const { mutate: sendResetLink, isPending } = accountApi.useForgotPassword(handleSent);

  useEffect(() => {
    if (secondsRemaining === 0) return;
    const timer = setTimeout(() => setSecondsRemaining((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsRemaining]);

  const onSubmit = (data: ForgotPasswordParams) => {
    setSentEmail(data.email);
    sendResetLink(data);
  };

  const handleResend = () => {
    sendResetLink({ email: sentEmail });
  };

  const handleUseDifferentEmail = () => {
    setView('form');
    setSecondsRemaining(0);
    reset();
  };

  return (
    <AuthLayout variant="centered">
      <BackToSignInLink />

      {view === 'sent' ? (
        <>
          {/* WEB-008: made literal -- page heading is an <h2> (correction
              sheet § 1); AuthLayout mounts no hero on this variant, so this
              screen renders no <h1> at all, matching the prototype. */}
          <h2 className="text-2xl font-semibold text-foreground">Check your email</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {`We sent a password reset link to ${sentEmail}. The link expires in ${TOKEN_TTL_MINUTES} minutes.`}
          </p>

          <Alert variant="info" className="mt-6">
            <HelpCircle className="h-4 w-4" />
            <AlertDescription>
              Didn't get it? Check your spam folder or resend using the link below.
            </AlertDescription>
          </Alert>

          <div className="mt-6 flex items-center gap-4">
            <Button
              type="button"
              variant="link"
              className="px-0"
              onClick={handleResend}
              disabled={secondsRemaining > 0 || isPending}
            >
              Resend link
            </Button>
            <Button type="button" variant="secondary" onClick={handleUseDifferentEmail}>
              Use a different email
            </Button>
          </div>
        </>
      ) : (
        <>
          <h2 className="text-2xl font-semibold text-foreground">Forgot your password?</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Enter the email tied to your Signal account and we'll send you a reset link.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6" noValidate>
            <div className="w-full space-y-2">
              <Label
                htmlFor="email"
                className={cn('block text-sm leading-none', errors.email && 'text-destructive')}
              >
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@brand.com"
                className={cn(errors.email && 'border-destructive focus-visible:ring-destructive')}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'email-error' : undefined}
                {...register('email')}
              />
              {errors.email && (
                <p id="email-error" className="leading-none text-destructive text-sm">
                  {errors.email.message}
                </p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending link…
                </>
              ) : (
                'Send reset link'
              )}
            </Button>
          </form>
        </>
      )}
    </AuthLayout>
  );
};

export default ForgotPassword;
